import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  Injectable,
} from '@angular/core';
import type { LngLatLike, MapboxGeoJSONFeature, Popup } from 'mapbox-gl';
import { CellGeoData } from './geocoder-cache';
import { LEVEL_LAYER, ORI_LAYER } from './layer-store.service';
import { MapBoxService } from '../mapbox.service';
import { schoolsPoiLayers } from '../../menu/right-menu/layers-menu/education/education.service';
import { MapHttpService } from './map-http.service';
import { DatePipe } from '@angular/common';
import { BreakpointObserverService } from '../../../shared/services/breakpoint-observer.service';
import { Feature, Position } from 'geojson';
import {
  DEFAULT_POPUP,
  HOVER_POPUP,
  MapPopupComponent,
  ORI_POPUP,
  PopupData,
  SCHOOLS_POPUP,
} from '../visualization/map-popup/map-popup.component';
import { SearchGeocoderService } from '../../../shared/services/search-geocoder.service';
import { SelectedCellService } from './selected-cell.service';
import { pointsLayersList } from '../../menu/right-menu/layers-menu/points.service';
import { ReportsService } from '../../../reports/reports.service';
import { geocoderContextToString } from '../../../shared/util/geocoder-context-to-string';
import { convertLayerIdToCellType } from '../../../shared/util/layerIdToCellType';
import { Router } from '@angular/router';
import { ReportTypes } from '../../../user/user/user.model';

declare const mapboxgl: typeof import('mapbox-gl');

/** Elements a drag may start from — see `enableDragOnPopup`. */
const DRAG_HANDLE_SELECTOR = '.drag-button, .place';

export const CLICK = 'CLICK';
export const HOVER = 'HOVER';

export type PopupActionType = typeof CLICK | typeof HOVER;

interface PopupOffsetConfig {
  offsetXFactor?: number;
  offsetYTopFactor?: number;
  offsetYMidFactor?: number;
  offsetYBottomFactor?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  public popupTitle!: string;
  public popup!: Popup;

  public isSeeMoreActive: boolean = true;

  public currentlySelectedId: string | null = null;
  public currentlySelectedLevel: LEVEL_LAYER | null = null;
  public popupComponentRef?: ComponentRef<MapPopupComponent>;

  private topPosition!: number;
  private leftPosition!: number;

  private userMovedPopup = false;

  constructor(
    private mapboxService: MapBoxService,
    private mapHttpService: MapHttpService,
    private datePipe: DatePipe,
    private isMobileService: BreakpointObserverService,
    private geocoderService: SearchGeocoderService,
    private selectedCellService: SelectedCellService,
    private appRef: ApplicationRef,
    private reportsService: ReportsService,
    private router: Router
  ) {}

  public handlePopup(
    feature: MapboxGeoJSONFeature,
    coordinates: LngLatLike,
    actionType: PopupActionType
  ): void {
    if (this.popup && this.popup.isOpen()) {
      this.popup.remove();
    }

    this.userMovedPopup = false;

    this.currentlySelectedId = feature.properties!.external_id;
    this.currentlySelectedLevel = feature.layer.id as LEVEL_LAYER;

    const data: PopupData = {
      type: this.getPopupType(feature, actionType),
      properties: { ...feature.properties, ...feature.state },
      feature: feature,
    };

    if (pointsLayersList.includes(feature.layer.id)) {
      this.handlePointsPopup(feature);
    }

    this.initPopup(coordinates);
    this.setPopupContent(data);
  }

  public getPopupType(
    feature: MapboxGeoJSONFeature,
    actionType: PopupActionType
  ): PopupData['type'] {
    return actionType === HOVER
      ? HOVER_POPUP
      : schoolsPoiLayers.includes(feature.layer.id)
        ? SCHOOLS_POPUP
        : feature.layer.id === ORI_LAYER
          ? ORI_POPUP
          : DEFAULT_POPUP;
  }

  public initPopup(coordinates: mapboxgl.LngLatLike): void {
    const isMobile = this.isMobileService.isMobile.getValue();
    const { anchor, offset } = {
      anchor: 'top' as mapboxgl.Anchor,
      offset: [0, 0] as mapboxgl.Offset,
    };

    // Assign popup instance on first use with params related to mobile/desktop
    // as they are not changebale after init and we want to keep single popup instance
    // to manipulate it from geocoderService
    if (isMobile) {
      if (!this.popup) {
        this.popup = new mapboxgl.Popup({
          anchor: anchor,
          offset: offset,
          // Widened from 345px: the popup now carries a tab strip and a
          // two-up stat grid, both of which were truncating at the old width.
          maxWidth: 'min(94vw, 460px)',
          className: 'default-popup',
          closeButton: false,
          closeOnClick: false,
        }).setLngLat(coordinates);
      }

      this.centerPopupOnMobile(coordinates);
    } else {
      if (!this.popup) {
        this.popup = new mapboxgl.Popup({
          maxWidth: '560px',
          className: 'default-popup',
          closeButton: false,
          closeOnClick: false,
        });
      }

      this.setDesktopPopupPosition(
        coordinates,
        this.mapboxService.map,
        this.popup
      );
    }
  }

  public setDesktopPopupPosition(
    coordinates: mapboxgl.LngLatLike,
    map: mapboxgl.Map,
    popup: mapboxgl.Popup,
    config?: PopupOffsetConfig
  ): void {
    if (this.userMovedPopup) {
      // if user moved it manually, do not auto reposition
      return;
    }

    // Defaults:
    const offsetXFactor = config?.offsetXFactor ?? 0.1;
    const offsetYTopFactor = config?.offsetYTopFactor ?? 0.25;
    const offsetYMidFactor = config?.offsetYMidFactor ?? 0.1;
    const offsetYBottomFactor = config?.offsetYBottomFactor ?? 0.1;

    // Project the coordinates to pixels
    const projected = map.project(coordinates);

    // Get map container sizes
    const mapContainer = map.getContainer();
    const mapWidth = mapContainer.offsetWidth;
    const mapHeight = mapContainer.offsetHeight;

    // Calculate percentages for x and y relative to map width and height
    const xPercentage = (projected.x / mapWidth) * 100;
    const yPercentage = (projected.y / mapHeight) * 100;

    const newPoint = { x: projected.x, y: projected.y };
    let offsetX = 0;
    let offsetY = 0;

    // Horizontal offset: adjust differently depending on which half of the map the point is in
    if (xPercentage > 50) {
      offsetX = -mapWidth * offsetXFactor;
    } else {
      offsetX = mapWidth * offsetXFactor;
    }

    // Vertical offset: use different factors based on the y position
    if (yPercentage < 30) {
      offsetY = mapHeight * offsetYTopFactor;
    } else if (yPercentage >= 30 && yPercentage < 80) {
      offsetY = -mapHeight * offsetYMidFactor;
    } else if (yPercentage >= 80) {
      offsetY = -mapHeight * offsetYBottomFactor;
    }

    newPoint.x += offsetX;
    newPoint.y += offsetY;

    // Unproject the new point and update the popup location
    const newCoord = map.unproject(newPoint as mapboxgl.PointLike);
    popup.setLngLat(newCoord);
  }

  public setPopupTopRight(
    map: mapboxgl.Map,
    popup: mapboxgl.Popup,
    margin: number = 10
  ): void {
    // Get the map container dimensions
    const mapContainer = map.getContainer();
    const mapWidth = mapContainer.offsetWidth;
    // Use the margin for both x and y directions
    const topRightPixel = { x: mapWidth - margin, y: 5 };
    // Convert pixel position to geographical coordinates
    const topRightCoord = map.unproject(topRightPixel as mapboxgl.PointLike);
    // Set the popup's location to the computed coordinates
    popup.setLngLat(topRightCoord);
  }

  private centerPopupOnMobile(coordinates: LngLatLike): void {
    const map = this.mapboxService.map;
    if (map) {
      map.flyTo({
        center: coordinates,
        zoom: map.getZoom(),
      });
    }
  }

  public handlePopupTitle(feature?: MapboxGeoJSONFeature): void {
    this.setTitle('');

    const selectedFeature =
      feature ?? this.selectedCellService.getSelectedFeature();

    const id = selectedFeature?.id;
    const center = this.mapboxService.getFeatureCenter(selectedFeature!);

    if (!this.geocoderService.geocoderCache.getCellData(id)) {
      this.handleGeocoderRequest(id, center).then((res) => {
        if (res) {
          this.geocoderService.geocoderCache.addCell(res.id, res.data);
        }
        // Unconditional: without an address `updateTitle` falls back to the
        // feature's own name, which is what the cached branch below shows
        // anyway. Skipping it on failure left the popup title blank forever.
        this.updateTitle(selectedFeature!);
      });
    } else {
      this.updateTitle(selectedFeature!);
    }
  }

  public reapplyCssPosition(): void {
    const el = this.popup?.getElement() as HTMLElement | null;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.setProperty('left', `${this.leftPosition}px`);
      el.style.setProperty('top', `${this.topPosition}px`);
    });
  }

  private setPopupContent(data: PopupData): void {
    this.popupComponentRef = createComponent(MapPopupComponent, {
      environmentInjector: this.appRef.injector,
    });

    this.popupComponentRef.instance.data = data;

    this.appRef.attachView(this.popupComponentRef.hostView);

    const domElem = (this.popupComponentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    this.popup.setDOMContent(domElem).addTo(this.mapboxService.map);

    let popupEl = this.popup.getElement() as HTMLElement;
    const map = this.mapboxService.map;

    const ro = new ResizeObserver((entries) => {
      popupEl = this.popup.getElement() as HTMLElement;
      if (!popupEl) return;

      // We will compute rawX, rawY differently depending on whether
      // popup already existed or it's new one
      let rawX: number, rawY: number;

      if (this.userMovedPopup) {
        // 1) Popup existed before: use current left/top as raw coords for reposition
        const currentLeft = parseFloat(popupEl.style.left || '0');
        const currentTop = parseFloat(popupEl.style.top || '0');
        rawX = currentLeft;
        rawY = currentTop;
      } else {
        // 2) Popup freshly created: project its geo‐coordinate
        //    bottom‐center point to screen, then offset by its dimensions:
        const coord = this.popup.getLngLat();
        const screenPt = map.project([coord.lng, coord.lat]);
        rawX = screenPt.x - popupEl.offsetWidth / 2;
        rawY = screenPt.y - popupEl.offsetHeight;
      }

      const { x: finalX, y: finalY } = this.getClampedCoordinates(
        map,
        popupEl,
        rawX,
        rawY,
        this.userMovedPopup
      );
      popupEl.style.left = `${finalX}px`;
      popupEl.style.top = `${finalY}px`;
    });

    ro.observe(popupEl);

    this.popup.on('close', () => {
      ro.unobserve(popupEl);
      this.appRef.detachView(this.popupComponentRef!.hostView);
      this.popupComponentRef!.destroy();
    });

    this.enableDragOnPopup();
  }

  /**
   * Makes the popup draggable by its handle or its bare background.
   *
   * Pointer events rather than a mouse pair plus a touch pair. The two
   * duplicated implementations had drifted — a fix to the mouse path silently
   * missed the touch one — and the touch half depended on `document` listeners
   * surviving a finger that slides off the handle, which is exactly what
   * `setPointerCapture` is for. One path now serves mouse, touch and pen, and
   * whatever is true of dragging on a laptop is true on a phone.
   *
   * A touch drag may only start somewhere that declares `touch-action: none`,
   * which the handle and the title do and `.popup-container` cannot: it is the
   * scroll parent of the tab panel, and `none` there would stop the panel
   * scrolling. `preventDefault()` on `pointerdown` is not a substitute — unlike
   * the `touchstart` handler this replaced, it does not suppress scrolling — so
   * the bare background stays a mouse-and-pen affordance.
   */
  private enableDragOnPopup() {
    const popupEl = this.popup.getElement() as HTMLElement;
    if (!popupEl) return;

    // The popup component renders on Angular's next tick, so the handle does
    // not exist yet on the frame the popup is added. Returning early here left
    // the popup permanently undraggable whenever that tick ran late.
    const attach = (attempt = 0): void => {
      const containerEl = popupEl.querySelector(
        '.popup-container'
      ) as HTMLElement | null;

      if (!containerEl) {
        if (attempt < 10) requestAnimationFrame(() => attach(attempt + 1));
        return;
      }

      const map = this.mapboxService.map;
      const mapContainer = map.getContainer() as HTMLElement;

      let pointerId: number | null = null;
      let dragOffsetX = 0;
      let dragOffsetY = 0;

      // The card's own background and its handles, never the content: a drag
      // starting inside the scrolling panel has to scroll it, not move the
      // popup.
      const isDragOrigin = (evt: PointerEvent) => {
        const el = evt.target as Element | null;
        if (!el) return false;
        if (el.closest(DRAG_HANDLE_SELECTOR)) return true;

        // See the note above: the background cannot opt out of the browser's
        // touch gestures, so a finger starting there would pull-to-refresh and
        // cancel the drag rather than move the card.
        return evt.pointerType !== 'touch' && el === containerEl;
      };

      const onPointerMove = (evt: PointerEvent) => {
        if (pointerId === null || evt.pointerId !== pointerId) return;
        evt.preventDefault();

        const mapRect = mapContainer.getBoundingClientRect();
        const { x, y } = this.getClampedCoordinates(
          map,
          popupEl,
          evt.clientX - mapRect.left - dragOffsetX,
          evt.clientY - mapRect.top - dragOffsetY,
          true
        );

        this.leftPosition = x;
        this.topPosition = y;
        popupEl.style.left = `${x}px`;
        popupEl.style.top = `${y}px`;
      };

      const endDrag = (evt: PointerEvent) => {
        if (pointerId === null || evt.pointerId !== pointerId) return;

        if (popupEl.hasPointerCapture(pointerId)) {
          popupEl.releasePointerCapture(pointerId);
        }

        pointerId = null;
        popupEl.style.cursor = 'unset';
      };

      const onPointerDown = (evt: PointerEvent) => {
        // Dragging while the map eases would fight mapbox for the popup's
        // position; a second finger is a map gesture, not a drag.
        if (
          pointerId !== null ||
          !evt.isPrimary ||
          map.isMoving() ||
          !isDragOrigin(evt)
        ) {
          return;
        }

        evt.preventDefault();
        this.userMovedPopup = true;
        popupEl.style.cursor = 'move';

        const popupRect = popupEl.getBoundingClientRect();
        dragOffsetX = evt.clientX - popupRect.left;
        dragOffsetY = evt.clientY - popupRect.top;
        pointerId = evt.pointerId;

        // Capture keeps the moves coming to this element once the finger
        // leaves it, which is what the old document-level listeners were
        // approximating. It throws if the pointer is no longer active, which
        // is not a reason to abandon the drag — uncaptured moves still arrive
        // while the pointer is over the popup.
        try {
          popupEl.setPointerCapture(evt.pointerId);
        } catch {
          /* not capturable; drag continues without it */
        }
      };

      // The uncaptured drag above is the one that can get stuck: its moves stop
      // arriving the moment the pointer leaves the popup, and with no
      // `pointerup` bound outside it nothing would ever clear `pointerId`. The
      // card would keep the `move` cursor and follow every later hover. Losing
      // the capture mid-drag lands in the same state, so it ends the drag too.
      const onPointerLeave = (evt: PointerEvent) => {
        if (pointerId !== null && popupEl.hasPointerCapture(pointerId)) return;
        endDrag(evt);
      };

      // Bound on the outer element only: everything inside bubbles to it, and
      // capturing there keeps `currentTarget` unambiguous for the release.
      popupEl.addEventListener('pointerdown', onPointerDown);
      popupEl.addEventListener('pointermove', onPointerMove);
      popupEl.addEventListener('pointerup', endDrag);
      popupEl.addEventListener('pointercancel', endDrag);
      popupEl.addEventListener('pointerleave', onPointerLeave);
      popupEl.addEventListener('lostpointercapture', endDrag);
    };

    requestAnimationFrame(() => attach());
  }

  /**
   * @param isUserDrag the map's own chrome is only avoided when *we* place the
   *   popup. Someone dragging it has the whole card in front of them and may
   *   well want it up by the search field; refusing to move above the
   *   auto-placed position just felt broken.
   */
  private getClampedCoordinates(
    map: mapboxgl.Map,
    popupEl: HTMLElement,
    rawX: number,
    rawY: number,
    isUserDrag = false
  ): { x: number; y: number } {
    const avoidChrome = this.isMobileService.isMobile.value && !isUserDrag;

    const mapRect = map.getContainer().getBoundingClientRect();

    const maxX = mapRect.width - popupEl.offsetWidth - 1;
    const minX = 1; // Make at least 1px away from navbar

    // On a phone the map runs edge to edge under its own chrome: the search
    // field sits 95px down and the bottom menu takes the last ~75px. Reserving
    // only the bottom put the top of a tall popup underneath the search field.
    // The matching cap on `.popup-container`'s height keeps this range
    // positive; the Math.max is what stops a popup that is somehow still too
    // tall from being pinned to a negative offset and bleeding off both ends.
    const chrome = avoidChrome
      ? this.readMobileChrome(popupEl)
      : { top: 0, bottom: 0 };

    const minY = chrome.top;
    const maxY = Math.max(
      minY,
      mapRect.height - popupEl.offsetHeight - chrome.bottom
    );

    let clampedX = rawX;
    let clampedY = rawY;

    if (clampedX < 0) clampedX = 0;
    if (clampedX > maxX) clampedX = maxX;
    if (clampedY < minY) clampedY = minY;
    if (clampedY > maxY) clampedY = maxY;
    if (clampedX < minX) clampedX = minX;

    return { x: clampedX, y: clampedY };
  }

  /**
   * The room the map's mobile chrome occupies, read from the card's own
   * `--popup-top-chrome` / `--popup-bottom-chrome`.
   *
   * The card already caps its `max-height` with those two custom properties, so
   * reading them here is what keeps the clamp and the cap describing one box.
   * Copying the numbers into TypeScript is what let the landscape override
   * (map-popup.component.scss) drift away from them: a landscape phone reserved
   * the portrait 138/64 against a 390px viewport, the range collapsed, and
   * every popup was pinned to the same y with its foot under the bottom menu.
   */
  private readMobileChrome(popupEl: HTMLElement): {
    top: number;
    bottom: number;
  } {
    const containerEl = popupEl.querySelector('.popup-container');
    if (!containerEl) return { top: 0, bottom: 0 };

    const styles = getComputedStyle(containerEl);

    return {
      top: parsePx(styles.getPropertyValue('--popup-top-chrome')),
      bottom: parsePx(styles.getPropertyValue('--popup-bottom-chrome')),
    };
  }

  public updatePopupContent(data: PopupData): void {
    if (this.popupComponentRef) {
      // Concat existing data with updated, reassign of data runs change detection
      this.popupComponentRef.instance.data = {
        type: data.type,
        properties: {
          ...this.popupComponentRef.instance.data.properties,
          ...data.properties,
        },
        feature: data.feature,
      };
      return;
    }
  }

  private async handleGeocoderRequest(
    featureID: Feature['id'],
    coordinates: Position
  ): Promise<{
    id: Feature['id'];
    data: CellGeoData;
  } | null> {
    const data = await this.geocoderService.reverseGeocodeViaPlugin(
      coordinates[1],
      coordinates[0]
    );

    return data ? { id: featureID, data } : null;
  }

  private updateTitle(feature: MapboxGeoJSONFeature): void {
    const id = feature.id;
    const levelLayer = feature.layer.id;
    const defaultName = feature!.properties!.name;
    const cellData = this.geocoderService.geocoderCache.getCellData(id);

    if (!cellData) {
      return;
    }

    let title = geocoderContextToString(cellData, levelLayer, defaultName);

    // Fallback to coordinates if no title could be constructed
    if (!title.trim()) {
      title = `lng: ${cellData.center[0].toFixed(
        2
      )} lat: ${cellData.center[1].toFixed(2)}`;
    }

    feature.properties!.addressToCompare = title;
    this.setTitle(title);
  }

  private handlePointsPopup(feature: MapboxGeoJSONFeature): void {
    this.popup.once('open', () => {
      this.assignSelectedStatus(true, feature);
    });

    this.popup.once('close', () => {
      this.assignSelectedStatus(false, feature);
    });
  }

  private assignSelectedStatus(
    status: boolean,
    feature: MapboxGeoJSONFeature
  ): void {
    this.mapboxService.map.setFeatureState(feature, { selected: status });
  }

  private setTitle(title: string): void {
    this.popupTitle = title;

    // Also set title for selected location in reports
    this.reportsService.selectedLocation = title;
  }

  public downloadXlsx(): void {
    if (!this.currentlySelectedId || !this.currentlySelectedLevel) {
      return;
    }

    this.mapHttpService
      .downloadXlsx(
        this.currentlySelectedId,
        convertLayerIdToCellType(this.currentlySelectedLevel)!
      )
      .subscribe((data) => {
        const currentTime = this.datePipe.transform(
          new Date(),
          'yyyy-MM-dd HH:mm:ss'
        );

        const blob: Blob = data as Blob;
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${this.currentlySelectedLevel}_${
          this.currentlySelectedId
        }_${currentTime}.xlsx`;
        link.click();
      });
  }

  public toggleSeeMore(): void {
    this.isSeeMoreActive = !this.isSeeMoreActive;
  }

  public handleRequestReport(): void {
    // The tile is the location and Community Compass is the report for it, so
    // there is nothing left to ask - open on the summary, where both are still
    // editable.
    this.reportsService.enterOrderFlow({
      at: 'review',
      type: ReportTypes.COMMUNITY_COMPASS,
      geometry: 'cell',
    });

    this.router.navigate(['/reports/order'], { queryParamsHandling: 'merge' });
  }

  public handleClose(): void {
    this.selectedCellService.deselectCell();
    this.userMovedPopup = false;
    this.popup.remove();
  }
}

/**
 * A CSS length in pixels, or 0 when the property is unset. Custom properties
 * come back as the raw token (`" 138px"`), which `parseFloat` handles.
 */
function parsePx(value: string): number {
  const parsed = parseFloat(value);
  return isFinite(parsed) ? parsed : 0;
}
